import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { CATEGORIES, TECHNOLOGIES, INDIAN_STATES, LISTING_TYPES } from "../lib/constants";
import { MapPin, Zap, Search, Sun, Wind, Droplets, Layers } from "lucide-react";

const TECH_ICON = { solar: Sun, wind: Wind, hydro: Droplets, hybrid: Layers, other: Zap };

export default function Browse() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get("q") || "");

  const category = params.get("category") || "all";
  const technology = params.get("technology") || "all";
  const state = params.get("state") || "all";
  const listingType = params.get("listing_type") || "all";

  const fetchListings = async () => {
    setLoading(true);
    try {
      const query = {};
      if (category !== "all") query.category = category;
      if (technology !== "all") query.technology = technology;
      if (state !== "all") query.state = state;
      if (listingType !== "all") query.listing_type = listingType;
      if (q) query.q = q;
      const { data } = await api.get("/listings", { params: query });
      setItems(data.items || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchListings();
    // eslint-disable-next-line
  }, [category, technology, state, listingType]);

  const setFilter = (k, v) => {
    const next = new URLSearchParams(params);
    if (v === "all" || !v) next.delete(k);
    else next.set(k, v);
    setParams(next);
  };

  const catLabel = useMemo(
    () => (category === "all" ? "All listings" : CATEGORIES.find((c) => c.value === category)?.label),
    [category]
  );

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-6 sm:py-10" data-testid="browse-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="label-caps">Marketplace</div>
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-primary tracking-tight mt-1">{catLabel}</h1>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            fetchListings();
          }}
          className="flex gap-2 w-full md:w-auto"
        >
          <div className="relative flex-1 md:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search title, description…"
              data-testid="browse-search"
              className="pl-9"
            />
          </div>
          <Button type="submit" data-testid="browse-search-btn" className="bg-primary hover:bg-primary/90">
            Search
          </Button>
        </form>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        <Select value={category} onValueChange={(v) => setFilter("category", v)}>
          <SelectTrigger data-testid="filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {CATEGORIES.map((c) => (
              <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={technology} onValueChange={(v) => setFilter("technology", v)}>
          <SelectTrigger data-testid="filter-technology"><SelectValue placeholder="Technology" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any technology</SelectItem>
            {TECHNOLOGIES.map((t) => (
              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={state} onValueChange={(v) => setFilter("state", v)}>
          <SelectTrigger data-testid="filter-state"><SelectValue placeholder="State" /></SelectTrigger>
          <SelectContent className="max-h-80">
            <SelectItem value="all">All states</SelectItem>
            {INDIAN_STATES.map((s) => (
              <SelectItem key={s} value={s}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={listingType} onValueChange={(v) => setFilter("listing_type", v)}>
          <SelectTrigger data-testid="filter-type"><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Buy & sell</SelectItem>
            {LISTING_TYPES.map((l) => (
              <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="py-24 text-center text-muted-foreground">Loading listings…</div>
      ) : items.length === 0 ? (
        <div className="py-24 text-center">
          <div className="font-display text-2xl text-primary mb-2">No listings yet.</div>
          <p className="text-muted-foreground">Be the first to post an opportunity.</p>
          <Link to="/dashboard/new">
            <Button className="mt-4 bg-primary hover:bg-primary/90">Post a listing</Button>
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5" data-testid="listings-grid">
          {items.map((it) => {
            const Icon = TECH_ICON[it.technology] || Zap;
            const img = it.image_paths?.[0];
            return (
              <Link to={`/listing/${it.id}`} key={it.id} data-testid={`listing-card-${it.id}`}>
                <Card className="overflow-hidden group hover:-translate-y-1 hover:shadow-md transition-all h-full">
                  <div className="aspect-[16/10] bg-secondary relative overflow-hidden">
                    {img ? (
                      <img src={fileUrl(img)} alt={it.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                    ) : (
                      <div className="w-full h-full grid place-items-center bg-gradient-to-br from-primary/10 to-accent/10">
                        <Icon className="h-14 w-14 text-primary/40" />
                      </div>
                    )}
                    <div className="absolute top-3 left-3 flex gap-2">
                      <Badge className="bg-white/90 text-primary border-0">{CATEGORIES.find((c) => c.value === it.category)?.label}</Badge>
                      {it.listing_type === "buy" && (
                        <Badge className="bg-accent/90 text-accent-foreground border-0">Requirement</Badge>
                      )}
                    </div>
                  </div>
                  <CardContent className="p-5">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <MapPin className="h-3.5 w-3.5" /> {it.state}
                      {it.technology && (
                        <>
                          <span>·</span>
                          <Icon className="h-3.5 w-3.5" />
                          <span className="capitalize">{it.technology}</span>
                        </>
                      )}
                    </div>
                    <h3 className="font-display text-xl font-semibold mt-2 line-clamp-2 group-hover:text-primary">
                      {it.title}
                    </h3>
                    <div className="mt-3 flex items-center justify-between text-sm">
                      <div>
                        {it.capacity_mw ? (
                          <span className="font-medium text-primary">{it.capacity_mw} MW</span>
                        ) : it.land_area_acres ? (
                          <span className="font-medium text-primary">{it.land_area_acres} acres</span>
                        ) : null}
                      </div>
                      <div className="text-muted-foreground">
                        {it.price_inr ? `₹ ${Number(it.price_inr).toLocaleString("en-IN")}` : "Price on request"}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
